import { memo, useCallback, useMemo, useState } from 'react'

import { string, number, object, SchemaOf } from 'yup'
import { Formik, Form, Field } from 'formik'

import { Grid, Paper } from '@mui/material'
import { DeleteOutlineOutlined, SaveOutlined } from '@mui/icons-material'

import { UploadContentType } from '@tabeeb/enums'
import { Button, DragAndDropFiles, PageHeader, PageLayout } from '@collaborate-health/uikit'
import { uploadFile } from '@tabeeb/services/uploadService'
import { FormikRichTextEditor } from '@tabeeb/shared/forms'
import { maxLengthWithName, noWhitespaces, requiredFieldWithName } from '@tabeeb/shared/utils/validationErrorMessages'

import { TrainingMaterialCreateRequest } from '@tabeeb/contracts/microservices/swagger-article-service'
import { FormSelect, FormTextField } from '@collaborate-health/forms'
import {
  ArticleContentType,
  ArticleReadScope,
  ArticleReadScopeDisplayName,
  ArticlePublicationStatus,
} from '../../../enums'

import PdfContent from '../../../components/ArticleContent/ArticlePdfContent'

const trainingMaterialSchema: SchemaOf<TrainingMaterialCreateRequest> = object().shape({
  Name: string()
    .strict()
    .trim(noWhitespaces)
    .max(256, maxLengthWithName('Name', 256))
    .required(requiredFieldWithName('Name')),
  Content: object()
    .shape({
      Type: number().oneOf([ArticleContentType.Html, ArticleContentType.Pdf]).required(),
      Content: string().required(),
    })
    .required(),
  ReadScope: number().oneOf([ArticleReadScope.Content, ArticleReadScope.System]).required(),
  Status: number().oneOf([ArticlePublicationStatus.Draft, ArticlePublicationStatus.Published]).required(),
})

export interface TrainingMaterialEditorPageProps {
  initial?: TrainingMaterialCreateRequest
  editing?: boolean
  title: string
  onSubmit: (data: TrainingMaterialCreateRequest) => void
}

const TrainingMaterialEditorPage: React.FC<TrainingMaterialEditorPageProps> = ({
  initial,
  editing = false,
  title,
  onSubmit,
}) => {
  const [uploadInProgress, setUploadInProgress] = useState(false)

  const onUploadFile = useCallback(async ({ file, onProgress }) => {
    try {
      setUploadInProgress(true)
      const { url, sas } = await uploadFile({
        file,
        uploadContentType: UploadContentType.ArticleDocument,
        onProgress,
        contentId: undefined,
        abortController: undefined,
      })

      return `${url.split('?')[0]}${sas.sasRead}`
    } finally {
      setUploadInProgress(false)
    }
  }, [])

  const initialValues = useMemo((): TrainingMaterialCreateRequest => {
    return {
      ...(initial || {}),
      Name: initial?.Name ?? '',
      Content: initial?.Content ?? {
        Type: ArticleContentType.Html,
        Content: '',
      },
      ReadScope: initial?.ReadScope ?? ArticleReadScope.Content,
      Status: initial?.Status ?? ArticlePublicationStatus.Draft,
    }
  }, [initial])

  const onSubmitForm = useCallback(
    (data) => {
      onSubmit({
        ...data,
        Name: data.Name,
        Content: data.Content,
        ReadScope: data.ReadScope,
        Status: data.Status,
      })
    },
    [onSubmit]
  )

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validateOnMount
      validationSchema={trainingMaterialSchema}
      onSubmit={onSubmitForm}
    >
      {({ values, setFieldValue, errors, isValid, isSubmitting }) => {
        const submitDisabled = !isValid || isSubmitting

        return (
          <Form style={{ display: 'flex', height: '100%', width: '100%' }} autoComplete='off'>
            <PageLayout background='default' spacing='page' component={Paper} maxWidth='lg'>
              <PageHeader
                title={title}
                actions={
                  <>
                    {values.Content.Type === ArticleContentType.Pdf && values.Content.Content && (
                      <Button
                        disabled={isSubmitting}
                        color='error'
                        variant='outlined'
                        startIcon={<DeleteOutlineOutlined />}
                        onClick={() => setFieldValue('Content.Content', null)}
                      >
                        Remove file
                      </Button>
                    )}
                    <Button
                      startIcon={<SaveOutlined />}
                      type='submit'
                      loading={isSubmitting}
                      disabled={submitDisabled}
                      color='primary'
                      size='small'
                      variant='contained'
                    >
                      Save
                    </Button>
                  </>
                }
              />
              <Grid container wrap='nowrap' direction='column' spacing={2} mt={-1} flexGrow={1} overflow='hidden'>
                <Grid item container direction='row' spacing={2}>
                  <Grid item xs={9}>
                    <FormTextField size='small' name='Name' label='Name' disabled={isSubmitting} />
                  </Grid>
                  <Grid item xs={3}>
                    <FormSelect
                      size='small'
                      name='ReadScope'
                      label='Read scope'
                      options={[ArticleReadScope.Content, ArticleReadScope.System].map((value) => ({
                        value,
                        name: ArticleReadScopeDisplayName[value],
                      }))}
                      disabled={editing || isSubmitting}
                    />
                  </Grid>
                </Grid>
                {values.Content.Type === ArticleContentType.Pdf && (
                  <Grid item xs={12} overflow='auto'>
                    {!values.Content.Content && (
                      <DragAndDropFiles
                        disabled={uploadInProgress || isSubmitting}
                        file={null!}
                        placeholder='Drag and drop a PDF file here or click to select a file'
                        fileUploadButtonText='Select file'
                        acceptMimeFormats={{ 'application/pdf': ['.pdf'] }}
                        acceptedFormatDisplayStrings={['pdf']}
                        onDropAccepted={async (acceptedFiles) => {
                          const url = await onUploadFile({ file: acceptedFiles[0] })

                          setFieldValue('Content.Content', url)
                        }}
                      />
                    )}
                    {values.Content.Content && <PdfContent url={values.Content.Content} />}
                  </Grid>
                )}
                {values.Content.Type === ArticleContentType.Html && (
                  <Grid item xs={12}>
                    <Field
                      name='Content.Content'
                      component={FormikRichTextEditor}
                      disabled={uploadInProgress || isSubmitting}
                      uploadsHandler={onUploadFile}
                    />
                  </Grid>
                )}
              </Grid>
            </PageLayout>
          </Form>
        )
      }}
    </Formik>
  )
}

export default memo(TrainingMaterialEditorPage)

